// Log a Sighting (desktop): a company spotted on the road, logged fast from the + button. Only the company name is required.
// logSighting() (targetModel) goes through the company door, records the sighting and makes the company a Researching target.
import React, { useState } from 'react';
import Modal from './Modal';
import Button from './Button';
import { useAppContext } from '../lib/AppContext';
import { logSighting } from '../modules/dashboard/targetModel';

const SLATE = 'var(--gov-slate-700)';
const field = { width: '100%', boxSizing: 'border-box', fontSize: 13, padding: '8px 10px', border: '1px solid var(--gov-slate-300)', borderRadius: 6, fontFamily: 'inherit' };
const label = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--gov-slate-600)', margin: '10px 0 4px' };

export default function LogSightingDialog({ open, onClose }) {
  const { activePartner, showToast } = useAppContext();
  const [form, setForm] = useState({ name: '', spottedWhere: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const [error, setError] = useState('');

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setDup(null); setError(''); };
  const close = () => { setForm({ name: '', spottedWhere: '', notes: '' }); setDup(null); setError(''); onClose(); };

  async function save(allowPossibleDuplicate = false) {
    if (!form.name.trim() || busy) return;
    setBusy(true); setError('');
    const r = await logSighting({ name: form.name, spottedWhere: form.spottedWhere.trim(), notes: form.notes.trim(), partnerId: activePartner?.id || null, allowPossibleDuplicate });
    setBusy(false);
    if (r.error) { setError(r.error); return; }
    if (r.status === 'possible_duplicate') { setDup((r.match && r.match.companies) || []); return; }
    const name = form.name.trim();
    close();
    if (showToast) showToast(r.target_error ? `Logged ${name}. It could not be made a target: ${r.target_error}` : `Logged ${name} as a sighting and a Researching target.`, r.target_error ? 'error' : 'success');
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Log a Sighting"
      width={460}
      footer={(
        <>
          <Button variant="secondary" onClick={close}>Cancel</Button>
          {dup
            ? <Button variant="primary" color={SLATE} onClick={() => save(true)} loading={busy} loadingText="Saving…">Log Anyway</Button>
            : <Button variant="primary" color={SLATE} onClick={() => save(false)} disabled={!form.name.trim()} loading={busy} loadingText="Saving…">Log Sighting</Button>}
        </>
      )}
    >
      <div style={{ fontSize: 13, color: 'var(--gov-slate-500)' }}>A company you spotted on the road. It becomes a Researching target.</div>
      <label style={label} htmlFor="sighting-name">Company Name</label>
      <input id="sighting-name" autoFocus value={form.name} onChange={set('name')} onKeyDown={(e) => { if (e.key === 'Enter') save(false); }} placeholder="Company on the truck or the sign" style={field} />
      <label style={label} htmlFor="sighting-where">Spotted Where</label>
      <input id="sighting-where" value={form.spottedWhere} onChange={set('spottedWhere')} placeholder="Highway, lease road, town (optional)" style={field} />
      <label style={label} htmlFor="sighting-notes">Notes</label>
      <textarea id="sighting-notes" value={form.notes} onChange={set('notes')} rows={3} placeholder="Optional" style={field} />
      {dup && (
        <div role="status" style={{ marginTop: 12, fontSize: 13, color: 'var(--gov-slate-700)', border: '1px solid var(--gov-slate-200)', borderRadius: 8, padding: '8px 10px' }}>
          Possible Duplicate: {dup.length ? dup.map((c) => c.name).join(', ') : 'a similar company'} is already in Charlie. Log Anyway adds it as its own company.
        </div>
      )}
      {error && <div role="alert" style={{ marginTop: 12, fontSize: 13, color: 'var(--gov-danger)' }}>{error}</div>}
    </Modal>
  );
}
